import type { FeatureContext } from './featureContext';
import type { EntityId } from '../sim/entity';
import {
  cardMasterRenderable,
  defaultNamedDeckIndex,
  PRACTICE_OPPONENT_ID,
  type CardDuelMove,
} from '../sim/index';
import type { CardDuelPromptMessage } from '../net/protocol';
import type {
  CardDuelStateMessage,
  WireCardBattleBeat,
  WireCardBattleEffectBeat,
  WireCardBattleTarget,
} from '../net/protocol';
import { initCardBattle, type CardBattleHandle } from '../cardBattle';
import { isPvpMenuTarget } from './pvpTarget';
import { CosmeticIconRenderer } from '../render/cosmeticIcon';
import { Direction } from '../sim/direction';
import { directionForDelta } from '../sim/direction';
import type { ArenaFxEvent } from '../sim/arena';
import type { ArenaProjectileKind } from '../sim/arenaWeapons';
import type { ProjectileEffectId } from '../sim/content';
import { cardPresentationProfile } from '../cardBattlePresentation';
import { cardMonsterCue } from '../audio/cardBattleCue';
import type { SoundId } from '../audio/soundObserver';

const CARD_DAMAGE_COLOR = 0xe2534a;
const CARD_HEAL_COLOR = 0x62d66f;

function arenaProjectile(effect: ProjectileEffectId): ArenaProjectileKind {
  switch (effect) {
    case 'fireball': return 'fireball';
    case 'fire-snake': return 'fire-snake';
    case 'bolt': return 'bolt';
    case 'arrow': return 'bow-arrow';
    default: return 'arrow';
  }
}

function worldEntityForSeat(state: CardDuelStateMessage, seat: 0 | 1): EntityId {
  return seat === state.yourSeat ? state.youId : state.opponentId;
}

function worldEntityForTarget(
  state: CardDuelStateMessage,
  target: WireCardBattleTarget | undefined,
): EntityId | undefined {
  return target?.kind === 'hero' ? worldEntityForSeat(state, target.seat) : undefined;
}

/** Present card semantics on the actual live-world player/Card Master sprites. */
function presentWorldBeat(
  ctx: FeatureContext,
  state: CardDuelStateMessage,
  beat: WireCardBattleBeat,
): void {
  if (ctx.ui.hidden) return;
  const renderer = ctx.renderer();
  if (!renderer) return;
  if (beat.kind === 'play') {
    if (beat.cardKind === 'monster') return;
    const caster = worldEntityForSeat(state, beat.seat);
    const target = worldEntityForTarget(state, beat.target)
      ?? worldEntityForSeat(state, beat.seat === 0 ? 1 : 0);
    const from = ctx.world()?.get(caster)?.position;
    const to = ctx.world()?.get(target)?.position;
    const facing = from && to
      ? directionForDelta(to.x - from.x, to.y - from.y) ?? from.facing
      : from?.facing ?? Direction.S;
    const profile = cardPresentationProfile(beat.cardId);
    renderer.playActionAnimation(caster, profile?.gesture === 'item' ? 'fidget' : 'cast', facing);
    return;
  }
  if (beat.kind !== 'effect') return;
  presentWorldEffect(ctx, state, beat);
}

function presentWorldEffect(
  ctx: FeatureContext,
  state: CardDuelStateMessage,
  beat: WireCardBattleEffectBeat,
): void {
  const renderer = ctx.renderer();
  if (!renderer) return;
  const target = worldEntityForTarget(state, beat.target)
    ?? (beat.seat === undefined ? undefined : worldEntityForSeat(state, beat.seat));
  if (target === undefined) return; // card minions present inside the battle gump
  const profile = cardPresentationProfile(beat.sourceCardId);
  if (beat.effect === 'damage') {
    const sourceSeat = beat.sourceSeat ?? (beat.target?.kind === 'hero'
      ? (beat.target.seat === 0 ? 1 : 0)
      : undefined);
    const source = sourceSeat === undefined ? undefined : worldEntityForSeat(state, sourceSeat);
    const from = source === undefined ? undefined : ctx.world()?.get(source)?.position;
    const to = ctx.world()?.get(target)?.position;
    if (profile?.projectile && from && to) {
      const fx: ArenaFxEvent = {
        kind: 'projectile',
        projectile: arenaProjectile(profile.projectile),
        sizeTier: 0,
        from: { x: from.x, y: from.y },
        to: { x: to.x, y: to.y },
        target,
        damage: beat.amount ?? 0,
        gen: 0,
      };
      renderer.playVolleyFx([fx], profile.color);
    } else if (profile?.impact) {
      renderer.playImpactFx(target, profile.impact, profile.color);
      renderer.showCombatText(target, String(beat.amount ?? 0), CARD_DAMAGE_COLOR);
    } else {
      renderer.showCombatText(target, String(beat.amount ?? 0), CARD_DAMAGE_COLOR);
    }
  } else if (beat.effect === 'heal') {
    renderer.playImpactFx(target, profile?.impact ?? 'greater-heal', profile?.color ?? CARD_HEAL_COLOR);
    renderer.showCombatText(target, `+${beat.amount ?? 0}`, CARD_HEAL_COLOR);
  } else if (beat.effect === 'ward') {
    renderer.playImpactFx(target, profile?.impact ?? 'greater-heal', profile?.color ?? 0xe5d28b);
    renderer.showCombatText(target, beat.action === 'gain' ? 'Ward' : 'Absorbed', 0xe5d28b);
  } else if (beat.effect === 'fatigue') {
    renderer.showCombatText(target, `Fatigue −${beat.amount ?? 0}`, CARD_DAMAGE_COLOR);
  } else if (beat.effect === 'delayed-damage' && beat.action === 'detonate') {
    renderer.playImpactFx(target, 'explosion', 0xff713c);
  }
}

/** Injectable seams for unit tests; defaults are the real modules. */
export interface CardDuelInstallDeps {
  initCardBattle?: typeof initCardBattle;
}

/**
 * The **PvP card duel** client feature (trading card game, PRD #493 Slice 5, ADR-0286):
 * the twin of {@link installDuel} for a card battle instead of melee. It wires
 *
 * - the **challenge flow** - a long-press "Card duel <name>" row (gated by the shared PvP
 *   row filter) that reuses the duel wager prompt with card-duel copy and sends a
 *   `CardDuelChallenge`;
 * - the **target's accept/decline prompt** (the owner-only `CardDuelPrompt`), reusing the
 *   same accept/decline window with card-duel copy, sending `CardDuelAccept`/`CardDuelDecline`;
 * - the **battle gump** (`#card-battle`), fed by the per-seat directed `CardDuelState`, that
 *   sends the in-match moves (`CardDuelAction`).
 *
 * The gump holds no game logic (ADR-0268): the server is authoritative on legality, escrow,
 * and outcome. Gated behind the same `cardsEnabled` launch flag the rest of the card game
 * is - while off, the menu row and prompts no-op.
 */
export function installCardDuel(ctx: FeatureContext, deps: CardDuelInstallDeps = {}): void {
  let enabled = false;
  let deckNames: string[] = [];
  let preferredDeckIndex: number | undefined;
  let pendingTarget: EntityId | undefined;
  let pendingPrompt: CardDuelPromptMessage | undefined;
  let soundMatchId: number | undefined;
  let soundedTurn: { matchId: number; turnNumber: number } | undefined;
  const preloadedCreatureSounds = new Set<SoundId>();
  const portraits = new CosmeticIconRenderer(
    ctx.assets.bodyFrames,
    ctx.assets.equipFrames,
    ctx.assets.hueTable,
    ctx.ui,
    ctx.assets.atlasStore,
  );

  const sendAction = (move: CardDuelMove): void => {
    const playerId = ctx.playerId();
    if (playerId !== undefined) {
      ctx.client()?.sendCommand({ kind: 'CardDuelAction', entity: playerId, move });
    }
  };

  const gump: CardBattleHandle = (deps.initCardBattle ?? initCardBattle)({
    doc: ctx.ui,
    onAction: sendAction,
    renderPortrait: async (entity, facing) => {
      // The practice AI has no world entity (Carl is a pure persona): render his
      // canned signature look so the foe portrait never sits empty.
      const look = entity === PRACTICE_OPPONENT_ID
        ? cardMasterRenderable()
        : ctx.world()?.get(entity)?.renderable;
      // Each duelist stands turned toward the middle of the arena, not at the viewer.
      return look ? portraits.renderOutfit(look, facing === 'east' ? Direction.E : Direction.W) : null;
    },
    onWorldBeat: (state, beat) => presentWorldBeat(ctx, state, beat),
    onSound: (cue) => ctx.audio.play(cue),
  });

  const preloadVisibleCreatureSounds = (msg: CardDuelStateMessage): void => {
    if (soundMatchId !== msg.matchId) {
      soundMatchId = msg.matchId;
      preloadedCreatureSounds.clear();
    }
    const added: SoundId[] = [];
    for (const card of [...msg.you.board, ...msg.foe.board]) {
      const identity = { cardId: card.cardId, rarity: card.rarity };
      for (const role of ['attack', 'hurt', 'die'] as const) {
        const cue = cardMonsterCue(identity, role);
        if (cue && !preloadedCreatureSounds.has(cue.sound)) {
          preloadedCreatureSounds.add(cue.sound);
          added.push(cue.sound);
        }
      }
    }
    if (added.length > 0) void ctx.audio.preload(added);
  };

  // Open the card-duel challenge flow for an already-known target (the duel installer's
  // `startDuel` twin), reusing the wager prompt with card-duel copy. Card duels are
  // reserved-only, so `canStake` also carries the "you have a durable balance" signal; the
  // server rules the target (self / rat / NPC / ghost / range / busy / reserved) and wager.
  const startCardDuel = (target: EntityId): void => {
    if (!enabled || ctx.playerId() === undefined) return;
    pendingTarget = target;
    ctx.client()?.openBinder();
  };

  const showChallengeSetup = (target: EntityId): void => {
    const opponentName = ctx.world()?.get(target)?.renderable?.name ?? 'that player';
    const gold = ctx.state.gold();
    ctx.state.duelUi.promptWager({
      opponentName,
      matchNoun: 'a card duel',
      maxGold: gold.durable ? gold.amount : 0,
      canStake: gold.durable,
      choices: deckNames,
      choiceLabel: 'Deck',
      initialChoice: preferredDeckIndex,
      onConfirm: (wager, deckIndex) => {
        const playerId = ctx.playerId();
        if (playerId !== undefined && deckIndex !== undefined) {
          ctx.client()?.sendCommand({ kind: 'CardDuelChallenge', entity: playerId, opponent: target, wager, deckIndex });
        }
      },
    });
  };

  // The long-press "Card duel <name>" row, gated by the shared PvP row filter (installed
  // after the melee "Duel" / dice "Roll the dice" rows - install order pins row order).
  ctx.menu.addEntityMenu((target) => {
    if (!enabled) return null;
    if (!isPvpMenuTarget(ctx.world(), ctx.playerId(), target.id, target.name)) return null;
    return { items: [{ label: `Card duel ${target.name}`, onSelect: () => startCardDuel(target.id) }] };
  });

  // The owner-only card-duel challenge prompt: open the accept/decline window with card-duel
  // copy, or dismiss it when the server withdraws / expires / resolves the challenge.
  ctx.events.on('cardDuelPrompt', (msg) => {
    if (!msg.open) {
      pendingPrompt = undefined;
      ctx.state.duelUi.dismissChallenge();
      return;
    }
    pendingPrompt = msg;
    ctx.client()?.openBinder();
  });

  ctx.events.on('cardState', (msg) => {
    deckNames = msg.decks.map((deck) => deck.name);
    preferredDeckIndex = defaultNamedDeckIndex(msg.decks);
    if (pendingTarget !== undefined) {
      const target = pendingTarget;
      pendingTarget = undefined;
      showChallengeSetup(target);
    }
    if (!pendingPrompt) return;
    const prompt = pendingPrompt;
    pendingPrompt = undefined;
    ctx.state.duelUi.showChallenge({
      challengerName: prompt.challengerName,
      matchNoun: 'a card duel',
      wager: prompt.wager,
      choices: deckNames,
      choiceLabel: 'Deck',
      initialChoice: preferredDeckIndex,
      onAccept: (deckIndex) => {
        const playerId = ctx.playerId();
        if (playerId !== undefined && deckIndex !== undefined) ctx.client()?.sendCommand({ kind: 'CardDuelAccept', entity: playerId, deckIndex });
      },
      onDecline: () => {
        const playerId = ctx.playerId();
        if (playerId !== undefined) ctx.client()?.sendCommand({ kind: 'CardDuelDecline', entity: playerId });
      },
    });
  });

  // The per-seat directed battle state drives the gump (open on a live match, close on abort).
  ctx.events.on('cardDuelState', (msg) => {
    void ctx.audio.warmMode(msg.open ? 'cards' : null);
    if (msg.open) {
      const starting = soundMatchId !== msg.matchId;
      preloadVisibleCreatureSounds(msg);
      if (starting) ctx.audio.play({ sound: 'card-duel-start', volume: 1 });
      const alreadySounded = soundedTurn?.matchId === msg.matchId
        && soundedTurn.turnNumber === msg.turnNumber;
      if (msg.phase === 'playing' && msg.yourTurn && !alreadySounded) {
        soundedTurn = { matchId: msg.matchId, turnNumber: msg.turnNumber };
        ctx.audio.play({ sound: 'card-your-turn', volume: 1 });
      }
    }
    gump.setState(msg);
  });
  // Target mode is client-only and must never cross a reconnect. The server's
  // fresh directed state will reopen the authoritative match immediately after.
  ctx.events.on('welcome', () => {
    soundMatchId = undefined;
    preloadedCreatureSounds.clear();
    void ctx.audio.warmMode(null);
    gump.close();
  });

  // The launch gate (ADR: gate the unreleased card game): while off, the menu row and prompts
  // no-op and the gump stays closed.
  const setEnabled = (next: boolean): void => {
    enabled = next;
    if (!enabled) {
      pendingTarget = undefined;
      pendingPrompt = undefined;
      soundMatchId = undefined;
      preloadedCreatureSounds.clear();
      void ctx.audio.warmMode(null);
      gump.close();
    }
  };
  ctx.events.on('cardsEnabled', setEnabled);
  setEnabled(false);
}
